import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { hospitalOnboardingService } from '../../services/hospitalOnboardingService';
import { isTokenExpired } from '../../utils/authUtils';

/**
 * OnboardingRouteGuard
 * Protects onboarding wizard routes (/hospital/onboarding/*).
 * Ensures that:
 * 1. An active user token or partner registration session exists. If unauthenticated, bounces to /login.
 * 2. An active, valid applicationId exists and is scoped to the current user (auto-recovers from backend if missing).
 * 3. Prevents unauthenticated users or leftover application IDs from loading another person's draft.
 */
export const OnboardingRouteGuard = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isResolvingApp, setIsResolvingApp] = useState(false);
  const [resolvedAppId, setResolvedAppId] = useState(null);
  const [, setTick] = useState(0);

  // Re-check authentication and token state on browser back/forward or focus
  useEffect(() => {
    const handleRecheck = () => setTick((t) => t + 1);
    window.addEventListener('pageshow', handleRecheck);
    window.addEventListener('focus', handleRecheck);
    return () => {
      window.removeEventListener('pageshow', handleRecheck);
      window.removeEventListener('focus', handleRecheck);
    };
  }, []);

  // 1. Check for token in storage
  const token =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('token') || localStorage.getItem('aps_jwt_token')
      : null;

  const isTokenValid = Boolean(token && !isTokenExpired(token));

  // 2. Check for valid active applicationId
  const urlAppId = searchParams.get('applicationId');
  const storedAppId =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('risa_current_application_id')
      : null;

  const candidateAppId = urlAppId || storedAppId || resolvedAppId;

  const hasValidAppId =
    typeof candidateAppId === 'string' &&
    candidateAppId.trim().length > 0 &&
    !candidateAppId.startsWith('RISA-APP-');

  // If user is authenticated but applicationId is missing from URL and localStorage (e.g. after direct login),
  // automatically look up their existing onboarding application from the backend before bouncing them.
  useEffect(() => {
    let isMounted = true;
    if (isTokenValid && !hasValidAppId && !isResolvingApp) {
      setIsResolvingApp(true);
      hospitalOnboardingService
        .getUserApplications()
        .then((apps) => {
          if (isMounted && Array.isArray(apps) && apps.length > 0) {
            const latestApp = apps[0];
            const targetId = latestApp.application_id;
            if (targetId) {
              if (typeof localStorage !== 'undefined') {
                localStorage.setItem('risa_current_application_id', targetId);
                if (latestApp.partner_type) {
                  localStorage.setItem('risa_partner_type', latestApp.partner_type);
                }
              }
              setResolvedAppId(targetId);
            }
          }
        })
        .catch((err) => {
          console.warn('Could not auto-recover onboarding applications:', err);
        })
        .finally(() => {
          if (isMounted) setIsResolvingApp(false);
        });
    }
    return () => {
      isMounted = false;
    };
  }, [isTokenValid, hasValidAppId, isResolvingApp]);

  if (isLoading || isResolvingApp) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If not authenticated at all (logged out, token expired), redirect strictly to /login
  if (!isTokenValid) {
    // Clean up any stale orphaned application ID
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('risa_current_application_id');
    }
    return (
      <Navigate
        to="/login"
        state={{
          from: location,
          reason: 'unauthenticated'
        }}
        replace
      />
    );
  }

  // If authenticated but user truly has no application in database after lookup, redirect to registration
  if (!hasValidAppId) {
    const partnerType =
      searchParams.get('type') ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem('risa_partner_type') : null) ||
      'hospital';

    return (
      <Navigate
        to={`/hospital/register?type=${partnerType}`}
        state={{
          from: location,
          warningMessage: 'Please register your organization to access or resume the onboarding application.'
        }}
        replace
      />
    );
  }

  return children;
};

export default OnboardingRouteGuard;
